import React, { useEffect, useState } from "react";
import { accountService } from "api/services/account-service";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import DetailsPanelFooter from "components/shared-ui/details-panel-footer/details-panel-footer";
import {
  IMFADevice,
  IMFADeviceRequest,
  IRegisterMFADeviceResponse,
} from "api/entities/mfa";
import { useApi } from "utils/hooks/use-api";
import { MFADevicePanel } from "./mfa-device-panel";
import MfaStatusSection from "components/settings/tabs/account-tab/sections/mfa-status-section/mfa-status-section";
import MfaDevicesSection from "components/settings/tabs/account-tab/sections/mfa-devices-section/mfa-devices-section";
import styles from "./mfa-panel.module.scss";
import { IAppState } from "redux/reducers";
import { connect } from "react-redux";

const mapStateToProps = (state: IAppState) => ({
  user: state.users.currentUser,
});

export interface IMfaPanelProps extends ReturnType<typeof mapStateToProps> {
  open: boolean;
  onClose: (recoveryCodes?: IRegisterMFADeviceResponse[]) => void;
}

const panelDescription = ` Multi-factor authentication (MFA) adds an extra layer of security to your
              account. Once enabled, you'll be required to verify your identity using an
              authentication app in addition to your password when signing in.`;

export const MfaPanel = connect(
  mapStateToProps,
  null,
)((props: IMfaPanelProps) => {
  const [api, isLoading] = useApi();

  const [enabled, setEnabled] = useState(props.user.mfaEnabled ?? false);
  const [initialEnabled, setInitialEnabled] = useState<boolean | null>(
    props.user.mfaEnabled ?? false,
  );
  const [originalDevices, setOriginalDevices] = useState<IMFADevice[]>([]);
  const [stagedDevices, setStagedDevices] = useState<
    (IMFADevice | IMFADeviceRequest)[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [mfaDevicePanelOpen, setMfaDevicePanelOpen] = useState(false);

  const hasDevice = stagedDevices.length > 0;
  const canAddDevice = stagedDevices.length < 1; // Hard limit of 1 device (staged)

  const fetchDevices = async () => {
    try {
      const fetchedDevices = await api(accountService.listMfaDevices());
      setOriginalDevices(fetchedDevices || []);
      setStagedDevices((fetchedDevices || []).slice());
      setEnabled(!!props.user.mfaEnabled);
      setInitialEnabled(!!props.user.mfaEnabled);
    } catch (error) {
      setOriginalDevices([]);
      setStagedDevices([]);
      setEnabled(false);
      setInitialEnabled(false);
    }
  };

  useEffect(() => {
    if (props.open) {
      setErrorMessage("");
      fetchDevices();
    }
  }, [props.open]);

  const handleDeleteDevice = (deviceIdOrIndex: string | number) => {
    // Stage deletion locally by removing from stagedDevices
    setStagedDevices((prev) => {
      if (typeof deviceIdOrIndex === "number") {
        const copy = prev.slice();
        copy.splice(deviceIdOrIndex, 1);
        return copy;
      }
      return prev.filter((d) => (d as IMFADevice).id !== deviceIdOrIndex);
    });
  };

  const handleToggleDeviceActive = (device: IMFADevice | IMFADeviceRequest) => {
    setStagedDevices((prev) =>
      prev.map((d) => {
        if ((d as IMFADevice).id && (device as IMFADevice).id) {
          if ((d as IMFADevice).id === (device as IMFADevice).id) {
            return { ...(d as IMFADevice), active: !(d as IMFADevice).active };
          }
        } else if (!(d as IMFADevice).id && !(device as IMFADeviceRequest).id) {
          // new staged device without id, match by name
          if (
            (d as IMFADeviceRequest).name === (device as IMFADeviceRequest).name
          ) {
            return {
              ...(d as IMFADeviceRequest),
              active: !(d as IMFADeviceRequest).active,
            };
          }
        }
        return d;
      }),
    );
  };

  const handleAddDeviceClose = (result?: IMFADeviceRequest) => {
    setMfaDevicePanelOpen(false);
    if (result) {
      // Give staged new device a temporary id so table actions can reference it
      if (!result.id) {
        result.id = `__new_${Date.now()}`;
      }
      // Stage the new device locally
      setStagedDevices((prev) => [...prev, result]);
    }
  };

  const handleSave = async () => {
    setErrorMessage("");
    // Validation: cannot enable MFA without a device
    if (enabled && !hasDevice) {
      setErrorMessage("Cannot enable MFA without a configured device");
      return;
    }

    try {
      // Determine diffs between originalDevices and stagedDevices
      const originalById = new Map<string, IMFADevice>();
      for (const d of originalDevices) originalById.set(d.id, d);

      const stagedIds = new Set<string>();
      const newDevices: IMFADeviceRequest[] = [];
      const updatePromises: Promise<any>[] = [];

      for (const d of stagedDevices) {
        const id = (d as IMFADevice).id;
        if (id) {
          // temporary staged ids start with __new_
          if (String(id).startsWith("__new_")) {
            newDevices.push({
              name: (d as IMFADeviceRequest).name,
              active: (d as IMFADeviceRequest).active,
              secret: (d as IMFADeviceRequest).secret,
            });
          } else {
            stagedIds.add(id);
            const orig = originalById.get(id);
            if (orig && orig.active !== (d as IMFADevice).active) {
              updatePromises.push(
                api(
                  accountService.updateMfaDevice(id, {
                    active: (d as IMFADevice).active,
                  }),
                ),
              );
            }
          }
        } else {
          newDevices.push(d as IMFADeviceRequest);
        }
      }

      // Register new devices (if any) in a single call
      let recoveryCodes: IRegisterMFADeviceResponse[] = [];
      if (newDevices.length > 0) {
        recoveryCodes = await api(
          accountService.registerAndEnableMFA({
            enabled: false,
            devices: newDevices,
          }),
        );
      }

      // Deleted devices: those present in original but not in staged
      const deletePromises: Promise<any>[] = [];
      for (const orig of originalDevices) {
        if (!stagedIds.has(orig.id)) {
          deletePromises.push(api(accountService.deleteMfaDevice(orig.id)));
        }
      }

      // Apply updates and deletes
      await Promise.all([...updatePromises, ...deletePromises]);

      // Finally, update account-level enabled state if it changed
      if (initialEnabled === null) {
        // if unknown, attempt to set based on enabled
        if (enabled) await api(accountService.enableMfa());
        else await api(accountService.disableMfa());
      } else if (enabled !== initialEnabled) {
        if (enabled) await api(accountService.enableMfa());
        else await api(accountService.disableMfa());
      }

      // Refresh to reflect committed state
      await fetchDevices();
      props.onClose(recoveryCodes);
    } catch (error) {
      setErrorMessage("Failed to save MFA settings");
    }
  };

  const handleRefreshCodes = async (deviceId: string) => {
    const codes = await api(accountService.refreshRecoveryCodes(deviceId));
    props.onClose([{ deviceID: deviceId, recoveryCodes: codes }]);
  };

  const handleCancel = () => {
    setErrorMessage("");
    props.onClose();
  };

  return (
    <>
      <DetailsPanel
        open={props.open}
        isLoading={isLoading}
        onCloseClick={handleCancel}
        title="Multi-Factor Authentication"
        className={styles.panel}
        description={panelDescription}
      >
        {props.open && (
          <div className={styles.root}>
            <MfaStatusSection
              enabled={enabled}
              onChange={setEnabled}
              testId="mfa-status-section"
            />

            <MfaDevicesSection
              stagedDevices={stagedDevices}
              loading={isLoading}
              onAddClick={() => setMfaDevicePanelOpen(true)}
              canAdd={canAddDevice}
              onToggleActive={handleToggleDeviceActive}
              onDelete={handleDeleteDevice}
              onRefreshCode={handleRefreshCodes}
              testId="mfa-devices-section"
            />
            {!canAddDevice && (
              <div className={styles.deviceLimitMessage}>
                You have reached the maximum number of MFA devices (1). Delete a
                device to add another.
              </div>
            )}

            <DetailsPanelFooter
              sticky={true}
              errorMessage={errorMessage}
              saveButtonText="Save"
              onSaveButtonClick={handleSave}
              onCancelButtonClick={handleCancel}
            />
          </div>
        )}
      </DetailsPanel>

      <MFADevicePanel
        open={mfaDevicePanelOpen}
        onClose={handleAddDeviceClose}
      />
    </>
  );
});
